'use client';

import { c as _c } from "react/compiler-runtime";
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { type Address, type ChainId, MathLib } from '@morpho-org/blue-sdk';
import { type InterfaceToken, parseApiAsset } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { InfoTooltip, MarketCell, VaultCell } from '@morpho-org/ui-v6/client';
import { CellSimpleText } from '@morpho-org/ui-v6/server';
import { formatters } from '@morpho-org/ui-v6/utils';
import { Icon, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { Droplet24 } from '@repo/ui/icons';
import { tableFeatures } from '@tanstack/react-table';
import { CellNetApy } from '@/components/common/Cells/CellNetApy';
import { TokenWithHoverCard } from '@/components/common/TokenWithHoverCard';
import { WarningIcon } from '@/components/common/WarningIcon';
import { APY_DURATION_LABEL } from '@/constants/avgApy';
import { getParsedValue } from '@/helpers/formatters';
import type { VaultV2ExposureTab, VaultV2ExposureTableRow } from '../hooks/useVaultV2ExposureData';
import VaultV2AbsoluteCapCell from '../components/VaultV2AbsoluteCapCell';
import VaultV2AllocationCell from '../components/VaultV2AllocationCell';
import VaultV2CapDonutSpacer from '../components/VaultV2CapDonutSpacer';
import VaultV2ExposurePendingTimelocksIndicator from '../components/VaultV2ExposurePendingTimelocksIndicator';
import VaultV2RelativeCapCell from '../components/VaultV2RelativeCapCell';
export const vaultV2ExposureTableFeatures = tableFeatures({});
export type VaultV2ExposureTableFeatures = typeof vaultV2ExposureTableFeatures;
export const vaultV2ExposureColumnIds = {
  item: 'adapter',
  underlyingLiquidity: 'underlyingLiquidity',
  liquidity: 'liquidity',
  utilization: 'utilization',
  relativeAllocation: 'relativeAllocation',
  allocation: 'allocationShare',
  absoluteCap: 'allocationUsd',
  relativeCap: 'relativeCap',
  apy: 'apy'
};
export const getVaultV2ExposureColumnLabels = ({
  tab,
  vaultSupplyToken
}: {
  tab: VaultV2ExposureTab;
  vaultSupplyToken: InterfaceToken | null | undefined;
}) => ({
  [vaultV2ExposureColumnIds.item]: tab === 'VaultsAndMarkets' ? 'Markets' : tab,
  [vaultV2ExposureColumnIds.underlyingLiquidity]: vaultSupplyToken ? `Underlying Liquidity (${vaultSupplyToken.symbol})` : 'Underlying Liquidity',
  [vaultV2ExposureColumnIds.liquidity]: vaultSupplyToken ? `Liquidity (${vaultSupplyToken.symbol})` : 'Liquidity',
  [vaultV2ExposureColumnIds.utilization]: 'Utilization',
  [vaultV2ExposureColumnIds.relativeAllocation]: 'Allocation (%)',
  [vaultV2ExposureColumnIds.allocation]: vaultSupplyToken ? `Allocation (${vaultSupplyToken.symbol})` : 'Allocation',
  [vaultV2ExposureColumnIds.absoluteCap]: 'Absolute Cap',
  [vaultV2ExposureColumnIds.relativeCap]: 'Relative Cap',
  [vaultV2ExposureColumnIds.apy]: 'APY'
});

/** Cap columns put the donut first, so an empty cap needs the same leading spacer. */
const VaultV2EmptyCapCell = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="gap-xs flex items-center"><VaultV2CapDonutSpacer /><CellSimpleText>-</CellSimpleText></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const liquidityAdapterTooltip = 'This is the default allocation for new deposits and is the first source for withdrawals beyond idle liquidity.';
const firstColumnHeaderTooltipContent: Record<VaultV2ExposureTab, ReactNode> = {
  VaultsAndMarkets: 'This table shows the vault’s exposure to Morpho Markets.',
  Collateral: 'This table shows the vault’s total exposure to each collateral asset across its underlying markets.',
  Adapters: 'This table shows the vault’s exposure to Morpho adapters.'
};
export const getColumnsVaultV2Exposure = ({
  tab,
  vaultSupplyToken,
  vaultAddress,
  chainId,
  hasOnlyMarketV1Adapters
}: {
  tab: VaultV2ExposureTab;
  vaultSupplyToken: InterfaceToken | null | undefined;
  vaultAddress: Address;
  chainId: ChainId;
  hasOnlyMarketV1Adapters?: boolean;
}): ColumnDef<VaultV2ExposureTableFeatures, VaultV2ExposureTableRow>[] => {
  const vaultV2ExposureColumnLabels = getVaultV2ExposureColumnLabels({
    tab,
    vaultSupplyToken
  });
  return [{
    id: vaultV2ExposureColumnIds.item,
    header: () => <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
              {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.item]}
            </span>
          </TooltipTrigger>
          <TooltipContent>{firstColumnHeaderTooltipContent[tab]}</TooltipContent>
        </Tooltip>,
    cell: ({
      row
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        item
      } = rowData;
      const hasPendingTimelocks = rowData.pendingTimelocks && rowData.pendingTimelocks.length > 0 || rowData.underlyingVaultPendingTimelocks && rowData.underlyingVaultPendingTimelocks.length > 0;
      const underlyingVault = item.type === 'VaultV1' ? {
        name: item.vault.name,
        address: item.vault.address,
        assetLogoURI: item.vault.assetLogoURI
      } : undefined;
      const pendingTimelocksIndicator = hasPendingTimelocks && rowData.caps ? <VaultV2ExposurePendingTimelocksIndicator chainId={chainId} pendingTimelocks={rowData.pendingTimelocks ?? []} underlyingVaultPendingTimelocks={rowData.underlyingVaultPendingTimelocks} underlyingVaultMarketSupplyCaps={rowData.underlyingVaultMarketSupplyCaps} underlyingVault={underlyingVault} caps={rowData.caps} vaultSupplyToken={vaultSupplyToken} /> : null;
      if (item.type === 'VaultV1') {
        return <div className="gap-xs flex items-center">
              <VaultCell nameClassName="max-w-[240px]" logoURI={vaultSupplyToken?.logoURI} address={item.vault.address} name={item.vault.name} chainId={vaultSupplyToken?.chain.id ?? chainId} isVaultV2={false} warnings={item.vault.warnings} />
              <InfoTooltip tooltipContent={<>
                    You&apos;re exposed to this vault&apos;s underlying markets and collateral.
                    Please review the vault V1 risk information.
                  </>} />
              {rowData.isLiquidityAdapter && <Tooltip>
                  <TooltipTrigger asChild>
                    <Icon className="text-secondary-foreground hover:text-foreground size-5" icon={<Droplet24 />} />
                  </TooltipTrigger>
                  <TooltipContent>{liquidityAdapterTooltip}</TooltipContent>
                </Tooltip>}
              {pendingTimelocksIndicator}
            </div>;
      }
      if (item.type === 'MarketV1') {
        return <div className="gap-xs flex items-center">
              <MarketCell collateralAsset={item.market.collateralAsset} loanAsset={item.market.loanAsset} lltv={item.market.lltv} renderWarnings={<WarningIcon warnings={item.market.warnings} />} />
              {rowData.isLiquidityAdapter && <Tooltip>
                  <TooltipTrigger asChild>
                    <Icon className="text-secondary-foreground hover:text-foreground size-5" icon={<Droplet24 />} />
                  </TooltipTrigger>
                  <TooltipContent>{liquidityAdapterTooltip}</TooltipContent>
                </Tooltip>}
              {pendingTimelocksIndicator}
            </div>;
      }
      if (item.type === 'Token') {
        const token = getParsedValue(item.token, parseApiAsset);
        if (!token) return <CellSimpleText>-</CellSimpleText>;
        return <div className="gap-xs flex items-center">
              <TokenWithHoverCard asset={token} />
              {pendingTimelocksIndicator}
            </div>;
      }

      // item.type === "Adapter"
      return <div className="gap-xs flex items-center">
            <CellSimpleText>{item.name}</CellSimpleText>
            {pendingTimelocksIndicator}
          </div>;
    }
  }, {
    id: vaultV2ExposureColumnIds.relativeAllocation,
    header: () => <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
              {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.relativeAllocation]}
            </span>
          </TooltipTrigger>
          <TooltipContent>Share of total vault assets currently allocated here.</TooltipContent>
        </Tooltip>,
    cell: ({
      row
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        allocation,
        totalAssets
      } = rowData;
      if (totalAssets === 0n) return <CellSimpleText>-</CellSimpleText>;
      const ratio = Math.min(1, Number(allocation) / Number(totalAssets));
      return <CellSimpleText>
            {ratio.toLocaleString('en-US', {
          style: 'percent',
          maximumFractionDigits: 2
        })}
          </CellSimpleText>;
    }
  }, {
    id: vaultV2ExposureColumnIds.allocation,
    header: () => <span className="text-3xs text-secondary-foreground" style={{
      lineHeight: 'normal'
    }}>
          {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.allocation]}
        </span>,
    cell: ({
      row
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        allocation
      } = rowData;
      return <VaultV2AllocationCell amount={allocation} token={vaultSupplyToken} />;
    }
  }, ...(tab !== 'Collateral' ? [{
    id: vaultV2ExposureColumnIds.liquidity,
    header: () => <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
                    {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.liquidity]}
                  </span>
                </TooltipTrigger>
                <TooltipContent>The amount of liquidity available for withdrawal.</TooltipContent>
              </Tooltip>,
    cell: ({
      row
    }: {
      row: {
        original: VaultV2ExposureTableRow;
      };
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        underlyingLiquidity,
        allocation,
        isIdle
      } = rowData;
      if (underlyingLiquidity == null) return <CellSimpleText>-</CellSimpleText>;
      if (isIdle) {
        return <VaultV2AllocationCell amount={allocation} token={vaultSupplyToken} />;
      }
      if (hasOnlyMarketV1Adapters) {
        const cappedLiquidity = allocation != null ? MathLib.min(underlyingLiquidity, allocation) : underlyingLiquidity;
        return <VaultV2AllocationCell amount={cappedLiquidity} token={vaultSupplyToken} />;
      }
      const cappedLiquidity = allocation != null ? MathLib.min(underlyingLiquidity, allocation) : underlyingLiquidity;
      return <VaultV2AllocationCell amount={cappedLiquidity} token={vaultSupplyToken} />;
    }
  } satisfies ColumnDef<VaultV2ExposureTableFeatures, VaultV2ExposureTableRow>, {
    id: vaultV2ExposureColumnIds.underlyingLiquidity,
    header: () => <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
                    {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.underlyingLiquidity]}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  The underlying liquidity of this vault or market (not including public allocator
                  liquidity).
                </TooltipContent>
              </Tooltip>,
    cell: ({
      row
    }: {
      row: {
        original: VaultV2ExposureTableRow;
      };
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        underlyingLiquidity,
        isIdle
      } = rowData;
      if (underlyingLiquidity == null || isIdle) return <CellSimpleText>-</CellSimpleText>;
      return <VaultV2AllocationCell amount={underlyingLiquidity} token={vaultSupplyToken} />;
    }
  } satisfies ColumnDef<VaultV2ExposureTableFeatures, VaultV2ExposureTableRow>] : []), ...(tab === 'VaultsAndMarkets' ? [{
    id: vaultV2ExposureColumnIds.utilization,
    header: () => <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
                    {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.utilization]}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Amount in % of supplied assets that has been borrowed on the market.
                </TooltipContent>
              </Tooltip>,
    cell: ({
      row
    }: {
      row: {
        original: VaultV2ExposureTableRow;
      };
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        utilization
      } = rowData;
      if (utilization == null) return <CellSimpleText>-</CellSimpleText>;
      return <CellSimpleText>
                  {formatters.percent.digits(2).unit('%').of(utilization)}
                </CellSimpleText>;
    }
  } satisfies ColumnDef<VaultV2ExposureTableFeatures, VaultV2ExposureTableRow>] : []), {
    id: vaultV2ExposureColumnIds.absoluteCap,
    header: () => <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
              {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.absoluteCap]}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Hard limits on allocation to specific protocols / markets.
          </TooltipContent>
        </Tooltip>,
    cell: ({
      row
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        absoluteCap,
        allocation
      } = rowData;
      if (!isDefined(absoluteCap)) return <VaultV2EmptyCapCell />;
      return <VaultV2AbsoluteCapCell absoluteCap={absoluteCap} allocation={allocation} token={vaultSupplyToken} index={row.index} />;
    }
  }, {
    id: vaultV2ExposureColumnIds.relativeCap,
    header: () => <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground" style={{
          lineHeight: 'normal'
        }}>
              {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.relativeCap]}
            </span>
          </TooltipTrigger>
          <TooltipContent>
            Percentage-based allocation limit relative to total vault assets.
          </TooltipContent>
        </Tooltip>,
    cell: ({
      row
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        relativeCap,
        allocation,
        totalAssets
      } = rowData;
      if (!isDefined(relativeCap)) return <VaultV2EmptyCapCell />;
      return <VaultV2RelativeCapCell relativeCap={relativeCap} allocation={allocation} totalAssets={totalAssets} index={row.index} />;
    }
  }, {
    id: vaultV2ExposureColumnIds.apy,
    header: () => <Tooltip>
          <TooltipTrigger asChild>
            <span className="text-3xs text-secondary-foreground" style={{
          marginLeft: '14px',
          lineHeight: 'normal'
        }}>
              {vaultV2ExposureColumnLabels[vaultV2ExposureColumnIds.apy]}
            </span>
          </TooltipTrigger>
          <TooltipContent>{`${APY_DURATION_LABEL} Supply APY + Rewards`}</TooltipContent>
        </Tooltip>,
    cell: ({
      row
    }) => {
      const rowData = row.original;
      if (rowData.type === 'category') {
        return null;
      }
      const {
        apy
      } = rowData;
      if (!apy) return <CellSimpleText style={{
        marginLeft: '14px'
      }}>-</CellSimpleText>;
      return <CellNetApy rewards={apy.rewards} netApy={apy.net} apy={apy.native} performanceFee={apy.performanceFee} variant={apy.variant} points={apy.points} asset={vaultSupplyToken} merklContext={{
        kind: 'vault',
        chainId,
        identifier: vaultAddress
      }} />;
    }
  }];
};